/******
 * This file was generated by a Rake task.
 * Do not modify this file manually.
 * Add changes to the `Area.ts` file.
 ******/
// oxlint-disable-next-line no-unused-vars
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
dayjs.extend(timezone);

import { Area } from "./Area";

import { AreaHasEquipment } from "./AreaHasEquipment";
import { CalendarEventResource } from "./CalendarEventResource";
import { MapObject } from "./MapObject";
import { NotificationList } from "./NotificationList";
import { Project } from "./Project";
import { WaypointDirection } from "./WaypointDirection";

export type attachmentMethod = "file" | "iframe";

export const attachmentMethodValues = { file: 0, iframe: 1 };

export abstract class AreaModel {
    id: number;
    archived: boolean;
    areaIframe: string | null;
    areaName: string | null;
    areasIndex: number | null;
    attachmentMethod: attachmentMethod;
    bannedVehicles: string[] | null;
    createdAt: Dayjs;
    defaultWaypoint: string | null;
    defaultWaypointAreaId: number | null;
    details: string | null;
    equipmentId: number | null;
    isDropOff: boolean | null;
    isWaypoint: boolean | null;
    matchJobsiteHours: boolean;
    projectId: number | null;
    sort: number | null;
    updatedAt: Dayjs;
    repeatingCalendarEventDefinitions: any | null;
    areaHasEquipment: AreaHasEquipment[] | undefined;
    calendarEventResources: CalendarEventResource[] | undefined;
    defaultWaypointArea: Area | null | undefined;
    defaultWaypointFor: Area[] | undefined;
    mapObject: MapObject | null | undefined;
    notificationLists: NotificationList[] | undefined;
    project: Project | null | undefined;
    waypointDirection: WaypointDirection | null | undefined;
    _destroy: boolean | undefined;

    /* eslint-disable max-statements */
    constructor(
        id: number,
        archived: boolean,
        areaIframe: string | null,
        areaName: string | null,
        areasIndex: number | null,
        attachmentMethod: attachmentMethod,
        bannedVehicles: string[] | null,
        createdAt: Dayjs,
        defaultWaypoint: string | null,
        defaultWaypointAreaId: number | null,
        details: string | null,
        equipmentId: number | null,
        isDropOff: boolean | null,
        isWaypoint: boolean | null,
        matchJobsiteHours: boolean,
        projectId: number | null,
        sort: number | null,
        updatedAt: Dayjs,
        repeatingCalendarEventDefinitions: any | null,
        areaHasEquipment: AreaHasEquipment[] | undefined,
        calendarEventResources: CalendarEventResource[] | undefined,
        defaultWaypointArea: Area | null | undefined,
        defaultWaypointFor: Area[] | undefined,
        mapObject: MapObject | null | undefined,
        notificationLists: NotificationList[] | undefined,
        project: Project | null | undefined,
        waypointDirection: WaypointDirection | null | undefined
    ) {
        this.id = id;
        this.archived = archived;
        this.areaIframe = areaIframe;
        this.areaName = areaName;
        this.areasIndex = areasIndex;
        this.attachmentMethod = attachmentMethod;
        this.bannedVehicles = bannedVehicles;
        this.createdAt = createdAt;
        this.defaultWaypoint = defaultWaypoint;
        this.defaultWaypointAreaId = defaultWaypointAreaId;
        this.details = details;
        this.equipmentId = equipmentId;
        this.isDropOff = isDropOff;
        this.isWaypoint = isWaypoint;
        this.matchJobsiteHours = matchJobsiteHours;
        this.projectId = projectId;
        this.sort = sort;
        this.updatedAt = updatedAt;
        this.repeatingCalendarEventDefinitions = repeatingCalendarEventDefinitions;
        this.areaHasEquipment = areaHasEquipment;
        this.calendarEventResources = calendarEventResources;
        this.defaultWaypointArea = defaultWaypointArea;
        this.defaultWaypointFor = defaultWaypointFor;
        this.mapObject = mapObject;
        this.notificationLists = notificationLists;
        this.project = project;
        this.waypointDirection = waypointDirection;
    }
    /* eslint-enable max-statements */

    /** Rails `alias_attribute :name, :area_name` */
    get name(): string | null {
        return this.areaName;
    }

    set name(value: string | null) {
        this.areaName = value;
    }

    // oxlint-disable-next-line complexity
    static fromApiObject(json: any, timezone: string): Area {
        const newObject = new Area(
            json["id"],
            json["archived"],
            json["area_iframe"],
            json["area_name"],
            json["areas_index"],
            json["attachment_method"],
            json["banned_vehicles"],
            dayjs(json["created_at"]).tz(timezone),
            json["default_waypoint"],
            json["default_waypoint_area_id"],
            json["details"],
            json["equipment_id"],
            json["is_drop_off"],
            json["is_waypoint"],
            json["match_jobsite_hours"],
            json["project_id"],
            json["sort"],
            dayjs(json["updated_at"]).tz(timezone),
            json["repeating_calendar_event_definitions"],
            json["area_has_equipment"]?.map((item: any) => AreaHasEquipment.fromApiObject(item, timezone)),
            json["calendar_event_resources"]?.map((item: any) => CalendarEventResource.fromApiObject(item, timezone)),
            json["default_waypoint_area"] == null
                ? json["default_waypoint_area"]
                : Area.fromApiObject(json["default_waypoint_area"], timezone),
            json["default_waypoint_for"]?.map((item: any) => Area.fromApiObject(item, timezone)),
            json["map_object"] == null ? json["map_object"] : MapObject.fromApiObject(json["map_object"], timezone),
            json["notification_lists"]?.map((item: any) => NotificationList.fromApiObject(item, timezone)),
            json["project"] == null ? json["project"] : Project.fromApiObject(json["project"], timezone),
            json["waypoint_direction"] == null
                ? json["waypoint_direction"]
                : WaypointDirection.fromApiObject(json["waypoint_direction"], timezone)
        );

        return newObject;
    }

    // oxlint-disable-next-line complexity
    toApiObject(visited = new Set<any>()): any {
        if (visited.has(this)) {
            // Return null, undefined, or primary key to break the cycle
            return undefined;
        }
        visited.add(this);

        return {
            id: this.id,
            archived: this.archived,
            area_iframe: this.areaIframe,
            area_name: this.areaName,
            areas_index: this.areasIndex,
            attachment_method: this.attachmentMethod,
            banned_vehicles: this.bannedVehicles,
            created_at: this.createdAt,
            default_waypoint: this.defaultWaypoint,
            default_waypoint_area_id: this.defaultWaypointAreaId,
            details: this.details,
            equipment_id: this.equipmentId,
            is_drop_off: this.isDropOff,
            is_waypoint: this.isWaypoint,
            match_jobsite_hours: this.matchJobsiteHours,
            project_id: this.projectId,
            sort: this.sort,
            updated_at: this.updatedAt,
            area_has_equipment_attributes: this.areaHasEquipment
                ?.map((item) => item.toApiObject(visited))
                .filter((item) => item !== undefined),
            calendar_event_resources_attributes: this.calendarEventResources
                ?.map((item) => item.toApiObject(visited))
                .filter((item) => item !== undefined),
            default_waypoint_area_attributes: this.defaultWaypointArea?.toApiObject(visited),
            default_waypoint_for: this.defaultWaypointFor
                ?.map((item) => item.toApiObject(visited))
                .filter((item) => item !== undefined),
            map_object_attributes: this.mapObject?.toApiObject(visited),
            notification_lists: this.notificationLists
                ?.map((item) => item.toApiObject(visited))
                .filter((item) => item !== undefined),
            project_attributes: this.project?.toApiObject(visited),
            waypoint_direction_attributes: this.waypointDirection?.toApiObject(visited),
            _destroy: this._destroy,
        };
    }

    // NOTE: This could potentially cause infinite loops if the object graph is
    // circular.
    inAlternateTimezone(timezone: string): Area {
        const clone = Object.create(this) as Area;

        for (const key of Object.keys(this)) {
            if (this[key] instanceof dayjs) {
                // @ts-expect-error: Typescript thinks that this is not a dayjs object even though it is checked on the line before
                clone[key] = this[key].tz(timezone);
            } else if (Array.isArray(this[key])) {
                clone[key] = this[key].map((item) =>
                    item.inAlternateTimezone ? item.inAlternateTimezone(timezone) : item
                );
            } else if (this[key] && this[key].inAlternateTimezone) {
                clone[key] = this[key].inAlternateTimezone(timezone);
            }
        }

        return clone;
    }
}
